import { Heart, Facebook, Instagram, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import iconLogo from "@/assets/icon-logo.webp";
import gphcLogo from "@/assets/gphc-logo.svg";
import icoLogo from "@/assets/ico-logo.webp";
import nhsLogo from "@/assets/nhs-logo.webp";

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);


const Footer = () => {
  const sitemapLinks = {
    Service: [
      { label: "How it works", href: "/how-it-works/" },
      { label: "Start now", href: "/#is-this-for-me" },
      { label: "Reviews", href: "/reviews/" },
      { label: "FAQs", href: "/faqs/" },
    ],
    Resources: [
      { label: "Get the Pill Online", href: "/get-the-pill-online/" },
      { label: "Free Contraception Online", href: "/guides/free-contraception-online/" },
      { label: "Is the Pill Free?", href: "/guides/is-the-contraceptive-pill-free/" },
      { label: "How to Get the Pill", href: "/guides/how-to-get-the-pill/" },
      { label: "Delay Your Period", href: "/period-delay-tablets/" },
      { label: "All Guides", href: "/guides/" },
      { label: "Missed Pill Tool", href: "/missed-pill/" },
      { label: "Pill Reminder", href: "/reminder/" },
    ],
    Legal: [
      { label: "Privacy Policy", href: "/privacy-policy/" },
      { label: "Terms & Conditions", href: "/terms/" },
      { label: "Cookie Policy", href: "/cookie-policy/" },
      { label: "Accessibility", href: "/accessibility/" },
    ],
    Support: [
      { label: "Contact Us", href: "/contact-us/" },
      { label: "FAQs", href: "/faqs/" },
      { label: "Complaints", href: "/contact-us/" },
      { label: "Feedback", href: "/contact-us/" },
    ],
  };

  return (
    <footer className="bg-navy text-white pb-28">
      {/* Sitemap */}
      <div className="py-12 px-6 border-b border-white/15">
        <div className="max-w-sm lg:max-w-6xl xl:max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-16">
            {Object.entries(sitemapLinks).map(([category, links]) => (
              <div key={category}>
                <h4 className="font-semibold text-sm mb-3 text-peach">{category}</h4>
                <ul className="space-y-2">
                   {links.map((link) => (
                     <li key={link.label}>
                       {link.href.startsWith("http") ? (
                         <a 
                           href={link.href} 
                           className="text-tiny text-white/70 hover:text-peach transition-colors"
                           target="_blank"
                           rel="noopener noreferrer"
                         >
                           {link.label}
                         </a>
                       ) : (
                         <Link 
                           to={link.href} 
                           className="text-tiny text-white/70 hover:text-peach transition-colors"
                         >
                           {link.label}
                         </Link>
                       )}
                     </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Regulatory badges */}
      <div className="py-8 px-6 border-b border-white/15">
        <div className="max-w-sm lg:max-w-6xl xl:max-w-7xl mx-auto">
          <p className="text-tiny text-white/50 text-center mb-4">Regulated & Certified by</p>
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <a href="https://www.pharmacyregulation.org/registers/pharmacy/9013012" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" aria-label="View our GPhC pharmacy registration (9013012)">
              <div className="w-18 h-18 bg-white rounded-lg flex items-center justify-center p-2">
                <img src={gphcLogo} alt="GPhC Registered Pharmacy 9013012" className="w-full h-full object-contain" loading="lazy" />
              </div>
              <span className="text-[9px] text-white/40 group-hover:text-peach transition-colors">GPhC Registered</span>
            </a>
            <a href="https://www.nhs.uk/services/online-only-pharmacy/pharmacy-services-direct/FJH02/contact-details-and-opening-times" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" aria-label="View our NHS service profile">
              <div className="w-18 h-18 bg-[#005EB8] rounded-lg flex items-center justify-center">
                <img src={nhsLogo} alt="NHS" className="w-12 h-auto" loading="lazy" />
              </div>
              <span className="text-[9px] text-white/40 group-hover:text-peach transition-colors">Providing NHS services</span>
            </a>
            <a href="https://ico.org.uk/ESDWebPages/Entry/ZA387968" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" aria-label="View our ICO data protection registration">
              <div className="w-18 h-18 bg-white rounded-lg flex items-center justify-center p-2">
                <img src={icoLogo} alt="ICO Registered" className="w-full h-full object-contain" loading="lazy" />
              </div>
              <span className="text-[9px] text-white/40 group-hover:text-peach transition-colors">ICO Registered</span>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="py-6 px-6">
        <div className="max-w-sm lg:max-w-6xl xl:max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-white rounded-lg p-1.5 flex-shrink-0">
                <img src={iconLogo} alt="Contraception Direct" className="h-6 w-auto" loading="lazy" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-tiny text-white/50">by</span>
                <span className="text-xs font-semibold text-white/80">PHARMACY SERVICES DIRECT</span>
              </div>
            </div>
            <div className="sm:ml-auto flex items-center gap-3 flex-wrap">
               <a href="https://www.facebook.com/61573724921001/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-white/10 hover:bg-peach/20 rounded-full flex items-center justify-center transition-colors" aria-label="Facebook">
                <Facebook className="w-4 h-4 text-primary" />
              </a>
              <a href="https://www.instagram.com/contraception.direct" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-white/10 hover:bg-peach/20 rounded-full flex items-center justify-center transition-colors" aria-label="Instagram">
                <Instagram className="w-4 h-4 text-primary" />
              </a>
              <a href="https://www.tiktok.com/@contraception.direct" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-white/10 hover:bg-peach/20 rounded-full flex items-center justify-center transition-colors" aria-label="TikTok">
                <TikTokIcon className="w-4 h-4 text-primary" />
              </a>
              <a href="https://x.com/GetThePillUK" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-white/10 hover:bg-peach/20 rounded-full flex items-center justify-center transition-colors" aria-label="X">
                <XIcon className="w-4 h-4 text-primary" />
              </a>
              <a href="https://www.linkedin.com/company/imaan-healthcare-limited/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-white/10 hover:bg-peach/20 rounded-full flex items-center justify-center transition-colors" aria-label="LinkedIn">
                <Linkedin className="w-4 h-4 text-primary" />
              </a>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-tiny text-white/60">
            <p>© 2026 Contraception Direct</p>
            <p className="flex items-center gap-1">
              Made with <Heart className="w-3 h-3 text-peach fill-peach" /> for women's health
            </p>
          </div>
          
          <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              <div>
                <p className="text-tiny text-white/70">
                  <span className="font-medium text-white/90">Imaan Ltd</span> · Company No. 06415284
                </p>
                <p className="text-tiny text-white/70 mt-1">
                  Trading as Contraception Direct & Pharmacy Services Direct
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-peach/80 font-semibold mb-0.5">Registered Pharmacy</p>
                <p className="text-tiny text-white/70">
                  <span className="font-medium text-white/90">Pharmacy Services Direct</span> · GPhC Premises No. 9013012
                </p>
                <p className="text-tiny text-white/50">
                  Unit 3 Micklehead Business Village, St. Michaels Road, Lea Green, St. Helens, Merseyside, WA9 4YU
                </p>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-peach/80 font-semibold mb-0.5">Superintendent Pharmacist</p>
                <p className="text-tiny text-white/70">
                  Abrar Rehman · GPhC No. 2044890
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;